import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import * as Styled from './ProgressMetrics.styled';
import { getDonorCount, formatPercentToGoal } from './utils';
import { StyledContentContainer } from '../../containers/StyledContentContainer.styled';
import { Progress } from '../../atoms/Progress';
import { Tooltip } from '../../atoms/Tooltip';
import { formatCurrency } from '../../features/passport/formatCurrency';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const SkeletonLoader = () => /*#__PURE__*/_jsxs(_Fragment, {
  children: [/*#__PURE__*/_jsx(Styled.LoaderAmountToGoal, {}), /*#__PURE__*/_jsx(Styled.LoaderBar, {})]
});
const ProgressBar = ({
  alignXPreset,
  cssBarColor,
  cssTrackColor,
  currency = 'USD',
  donorCount = 0,
  percentToGoal = 0,
  goal = 0,
  totalRaised = 0,
  showAmountToGoal = true,
  showDonorCount = true,
  campaignLocale = 'en-US',
  ...rest
}) => {
  const {
    t
  } = useTranslation('blocks.progressMetrics');

  /**
   * text-align for this block is not controlled by the user or passed as a prop,
   * but is instead computed based on the value of alignXPreset
   */
  const computedCssTextAlign = alignXPreset === 'right' ? 'right' : 'left';
  const percentageValue = percentToGoal > 5 ? percentToGoal : 5;
  const shouldRenderDonorCount = !!(showDonorCount && donorCount > 0 && percentToGoal > 0);
  const shouldRenderAmountRaised = showAmountToGoal && goal;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [shouldRenderAmountRaised && /*#__PURE__*/_jsx(_Fragment, {
      children: percentToGoal > 0 ? /*#__PURE__*/_jsx(Styled.AmountToGoal, {
        cssTextAlign: computedCssTextAlign,
        children: /*#__PURE__*/_jsx(Trans, {
          t: t,
          i18nKey: "ProgressMetrics.amountRaised",
          defaults: "<raised>{{raised}}</raised> raised of <goal>{{goal}}</goal> goal",
          values: {
            raised: formatCurrency({
              currencyCode: currency,
              amount: totalRaised,
              locale: campaignLocale,
              options: {
                notation: 'standard'
              }
            }).replace(/.00$/, ''),
            goal: formatCurrency({
              currencyCode: currency,
              amount: goal,
              locale: campaignLocale,
              options: {
                notation: goal >= 1000000 ? 'compact' : 'standard'
              }
            }).replace(/.00$/, '')
          },
          components: {
            raised: /*#__PURE__*/_jsx("span", {}),
            goal: /*#__PURE__*/_jsx("span", {})
          }
        })
      }) : /*#__PURE__*/_jsx(Styled.AmountToGoal, {
        cssTextAlign: computedCssTextAlign,
        children: /*#__PURE__*/_jsx(Trans, {
          t: t,
          i18nKey: "ProgressMetrics.toGoal",
          defaults: "<goal>{{goal}}</goal> to goal",
          values: {
            goal: formatCurrency({
              currencyCode: currency,
              amount: goal,
              locale: campaignLocale,
              options: {
                notation: goal >= 1000000 ? 'compact' : 'standard'
              }
            }).replace(/.00$/, '')
          },
          components: {
            goal: /*#__PURE__*/_jsx("span", {})
          }
        })
      })
    }), percentToGoal > 0 ? /*#__PURE__*/_jsx(Tooltip, {
      text: formatPercentToGoal(percentToGoal),
      placement: "left",
      children: /*#__PURE__*/_jsx(Progress, {
        "aria-label": t('ProgressMetrics.progressBar.ariaLabel', '{{percent}} of goal raised', {
          percent: formatPercentToGoal(percentToGoal)
        }),
        value: percentageValue,
        max: 100,
        cssProgressColor: cssBarColor,
        cssBackgroundColor: cssTrackColor,
        ...rest
      })
    }) :
    /*#__PURE__*/
    /* Always show at least 5% of the bar even if there is no progress */
    _jsx(Progress, {
      "aria-label": t('ProgressMetrics.progressBar.ariaLabelZero', '0% of goal raised'),
      value: percentageValue,
      max: 100,
      cssProgressColor: cssBarColor,
      cssBackgroundColor: cssTrackColor,
      ...rest
    }), shouldRenderDonorCount && /*#__PURE__*/_jsx(Styled.DonorCount, {
      cssTextAlign: computedCssTextAlign,
      children: t('ProgressMetrics.gift', {
        count: donorCount,
        donorCount: getDonorCount(donorCount),
        defaultValue: '{{donorCount}} gifts'
      })
    })]
  });
};

/**
 * The `ProgressMetrics` block visually displays the progress a campaign has made.
 *
 * Hovering over the progress bar will show a tooltip showing the percentage of the goal achieved.
 */
export const ProgressMetrics = ({
  alignXPreset,
  cssColor,
  currency,
  goal,
  parentLayoutInfo,
  ...rest
}) => /*#__PURE__*/_jsx(StyledContentContainer, {
  parentLayoutInfo: parentLayoutInfo,
  children: /*#__PURE__*/_jsx(Styled.ProgressLabel, {
    alignXPreset: alignXPreset,
    color: cssColor,
    children: goal ? /*#__PURE__*/_jsx(ProgressBar, {
      currency: currency,
      alignXPreset: alignXPreset,
      goal: goal,
      ...rest
    }) : /*#__PURE__*/_jsx(SkeletonLoader, {})
  })
});